import { gql, useMutation } from '@apollo/client';
import { ORDER_FRAGMENT } from '@/contexts/new-order-context-queries';
import { OriginalVariantPack } from '@/components/orders/substitution-item-wrapper';
import { QuantityMapType } from '@/screens/new-order/hooks/use-quantity-reducer';
import { SubstitutionAction } from '@/enums';
import {
  SubstitutionEntryInput,
  Substitutions_SetItemSubstitutionMutation,
  Substitutions_SetItemSubstitutionMutationVariables,
} from '@/types';
import { useCallback, useMemo } from 'react';

export const SET_ITEM_SUBSTITUTION_MUTATION = gql`
  mutation Substitutions_SetItemSubstitution(
    $action: String!
    $orderUUID: uuid!
    $sourceVariantPackUUID: uuid!
    $substitutionEntries: [SubstitutionEntryInput!]!
  ) {
    setItemSubstitution(
      action: $action
      order_id: $orderUUID
      source_variant_pack_id: $sourceVariantPackUUID
      substitution_entries: $substitutionEntries
    ) {
      order {
        ...NewOrder_OrderFields
      }
    }
  }

  ${ORDER_FRAGMENT}
`;

type SetSubstitutionArguments = {
  orderUUID: string;
  originalVariantPack: OriginalVariantPack;
} & (
  | {
      action: SubstitutionAction.SUBSTITUTE;
      quantityMap: QuantityMapType;
    }
  | {
      action: SubstitutionAction.DO_NOT_REPLACE;
      quantityMap?: undefined;
    }
);

export const useSetItemSubstitution = () => {
  const [setSubstitution, mutationResult] = useMutation<
    Substitutions_SetItemSubstitutionMutation,
    Substitutions_SetItemSubstitutionMutationVariables
  >(SET_ITEM_SUBSTITUTION_MUTATION);

  const setSubstitutionCallback = useCallback(
    async ({
      action,
      orderUUID,
      originalVariantPack,
      quantityMap,
    }: SetSubstitutionArguments) => {
      const substitutionEntries: SubstitutionEntryInput[] =
        action === SubstitutionAction.DO_NOT_REPLACE
          ? [
              {
                requested_quantity: originalVariantPack.requested_quantity,
                variant_pack_id: originalVariantPack.uuid,
              },
            ]
          : Object.keys(quantityMap).reduce<SubstitutionEntryInput[]>(
              (acc, variantPackUUID) => {
                const quantity = quantityMap[variantPackUUID];
                if (quantity > 0) {
                  acc.push({
                    requested_quantity: quantity,
                    variant_pack_id: variantPackUUID,
                  });
                }
                return acc;
              },
              [],
            );

      await setSubstitution({
        variables: {
          action,
          orderUUID,
          sourceVariantPackUUID: originalVariantPack.uuid,
          substitutionEntries,
        },
      });
    },
    [setSubstitution],
  );

  return useMemo(() => {
    return {
      ...mutationResult,
      setItemSubstitution: setSubstitutionCallback,
    };
  }, [mutationResult, setSubstitutionCallback]);
};
